import { z } from 'zod';
/** A booking-sheet deliverable on a booked role (deal memo, wardrobe form, …) with a due date. */
export const bookingSheetInputSchema = z.object({
    name: z.string().trim().min(1).max(160),
    due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    due_time: z.string().trim().max(20).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    done: z.boolean().optional(),
});
/** Common booking-sheet starters offered in the UI (free text also allowed). */
export const BOOKING_SHEET_PRESETS = [
    'Deal memo',
    'Booking confirmation',
    'Wardrobe form',
    'Work permit',
    'Size sheet',
    'Contract',
];
// ── Bookkeeping (QuickBooks-style money workflow over booked roles) ──────────────────
/** The bookkeeping column a booked job sits in. Closing auto-moves a card to "closed". */
export const BOOKKEEPING_STATUSES = ['open', 'awaiting', 'closed'];
export const BOOKKEEPING_STATUS_LABELS = {
    open: 'Open',
    awaiting: 'Awaiting Payment',
    closed: 'Closed',
};
/** Built-in payment methods; businesses can add their own custom labels on top. */
export const PAYMENT_METHODS = ['zelle', 'venmo', 'check', 'ach', 'credit_card', 'wire', 'cash', 'paypal'];
export const PAYMENT_METHOD_LABELS = {
    zelle: 'Zelle',
    venmo: 'Venmo',
    check: 'Check',
    ach: 'ACH',
    credit_card: 'Credit card',
    wire: 'Wire',
    cash: 'Cash',
    paypal: 'PayPal',
};
/** The two ends of a payment leg — who paid (from) and who got it (to). */
export const PAYMENT_PARTY_KINDS = ['production', 'agency', 'client', 'contact'];
export const PAYMENT_PARTY_LABELS = {
    production: 'Production company',
    agency: 'Agency',
    client: 'Client (talent)',
    contact: 'Contact',
};
const partyKindSchema = z.enum(PAYMENT_PARTY_KINDS);
/** A single recorded payment leg on a booked job. Method is free text (built-in key or custom label). */
export const bookingPaymentInputSchema = z.object({
    amount: z.number().nonnegative().nullable().optional(),
    method: z.string().trim().max(60).nullable().optional(),
    paid_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    from_kind: partyKindSchema.nullable().optional(),
    from_client_id: z.string().uuid().nullable().optional(),
    from_contact_id: z.string().uuid().nullable().optional(),
    from_label: z.string().trim().max(160).nullable().optional(),
    to_kind: partyKindSchema.nullable().optional(),
    to_client_id: z.string().uuid().nullable().optional(),
    to_contact_id: z.string().uuid().nullable().optional(),
    to_label: z.string().trim().max(160).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
});
/** Bookkeeping fields a bookkeeper can patch on a booked job. */
export const bookkeepingPatchSchema = z.object({
    bk_status: z.enum(BOOKKEEPING_STATUSES).optional(),
    booking_amount: z.number().nonnegative().nullable().optional(),
    booking_commission_pct: z.number().min(0).max(100).nullable().optional(),
    booking_notes: z.string().max(4000).nullable().optional(),
});
//# sourceMappingURL=booking.js.map